import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, useListRef } from 'react-window';
import { compileFilter } from '@progress/kendo-data-query';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import {
    CardHandle,
    ExpansionPanel,
    ExpansionPanelActionEvent,
    ExpansionPanelContent,
} from '@progress/kendo-react-layout';
import cn from 'clsx';
import * as utils from './filter.service';
import {
    calculateFiltersFromTrackedChanges,
    getCategoryValue as getCategoryValueForCategory,
    getExpansionPanelTitle,
} from './filter.service';
import {
    CategoryType,
    FieldDefinitionFieldTypeNumeric,
    FilterCategory,
    FilterCategoryItems,
    FilterHandle,
    FilterProps,
    FilterUIExclusion,
    FilterUIStateUpdate,
    FilterValue,
} from './interfaces';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import { InfoField } from '@components-clean/core/info-field';
import InteractiveList from '@components-clean/core/interactive-list';
import Loading from '@components-clean/core/loading/loading';
import { SearchBox } from '@components-clean/core/search-box';
import { AdvancedSelect } from '@components-clean/core/study-data-filter/advanced-select/advanced-select';
import { AdvanceSelectFilter } from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import styles from '@components-clean/core/study-data-filter/study-data-filter.module.scss';
import Tooltip from '@components-clean/core/tooltip';
import { useOnclickOutside } from '@hooks-clean/use-onclick-outside';
import { PopupService } from '@utils-clean/popup-service';
import { convertBooleanToYesNo, stringDateWithoutTimeFromUtc } from '@utils/convertor';

const CHECKBOX_SIZE = 23;
const CHECKBOX_ROW_PADDING = 5;
const ROW_SIZE = CHECKBOX_SIZE + CHECKBOX_ROW_PADDING;
const LIST_HEIGHT = 320;

export const FilterUI = forwardRef<FilterHandle, FilterProps>(
    (
        {
            categories,
            data,
            'data-testid': testid,
            onApply,
            defaultCategory,
            onClose,
            filterChipElements,
            currentTrackedChangesList,
            headerExtra,
        },
        ref,
    ) => {
        const [filterCategories, setFilterCategories] = useState<Array<FilterCategory>>(categories);
        const [selectedCategory, setSelectedCategory] = useState<FilterCategoryItems | undefined>();
        const selectedCategoryParent = useRef<CategoryType>(undefined);
        const [categoryFilter, setCategoryFilter] = useState<string>('');
        const [itemFilter, setItemFilter] = useState<string>('');
        const [checkboxes, setCheckboxes] = useState<Array<FilterValue>>([]);
        const [expanded, setExpanded] = useState<CategoryType | undefined>();
        const [showAdvancedSelect, setShowAdvancedSelect] = useState<boolean>(false);
        const [filterValues, setFilterValues] = useState<Array<FilterValue>>([]);
        const [trackedChanges, setTrackedChanges] = useState<FilterUIStateUpdate>({
            fieldKey: undefined,
            valueKey: undefined,
            uncheckedValues: [],
            dataType: FieldDefinitionFieldTypeNumeric.Undefined,
        });
        const [trackedChangesList, setTrackedChangesList] = useState<FilterUIStateUpdate[]>(
            currentTrackedChangesList.current,
        );
        const [showLoading, setShowLoading] = useState<boolean>(false);

        const listRef = useListRef(null);
        const advancedSelectAnchor = useRef<HTMLDivElement | null>(null);
        const cardRef = useRef<CardHandle>(null);
        const advancedSelectRef = useRef<HTMLDivElement>(null);
        const trackChangesChanged = useRef<boolean>(false);
        const { t }: { t: (key: string) => string } = useTranslation();

        const calculateLabel = (catInfo: FilterCategoryItems, value: unknown) => {
            switch (catInfo.dataType) {
                case FieldDefinitionFieldTypeNumeric.Date:
                    return value === null
                        ? 'Blank'
                        : (stringDateWithoutTimeFromUtc(String(value), 'DD-MMM-YYYY') ?? '');
                case FieldDefinitionFieldTypeNumeric.Bool:
                    return convertBooleanToYesNo(Boolean(value));
                default:
                    return value === null ? 'Blank' : String(value);
            }
        };

        const getCategoryValue = (v: unknown) =>
            selectedCategory ? getCategoryValueForCategory(selectedCategory, v) : undefined;

        const changeSelectedCategory = (parentCategory: CategoryType, categoryId: number) => {
            setExpanded(parentCategory);
            selectedCategoryParent.current = parentCategory;
            setSelectedCategory(
                categories?.find((c) => c.id === parentCategory)?.items.find((i) => i.id === categoryId),
            );
        };

        useImperativeHandle(ref, () => ({
            setSelectedCategory: changeSelectedCategory,
        }));

        const calculateFilters = (categoryToExclude?: FilterCategoryItems) =>
            calculateFiltersFromTrackedChanges(trackedChangesList, categoryToExclude, filterCategories);

        const filteredDataExcludingSelectedCategory = useMemo(() => {
            const predicate = compileFilter({
                logic: 'and',
                filters: calculateFilters(selectedCategory),
            });
            return data.filter(predicate);
        }, [selectedCategory]);

        useEffect(() => {
            const items = categories
                .map((item) => {
                    return {
                        id: item.id,
                        items:
                            item.items.filter(
                                (x) => x.label.toLowerCase().indexOf(categoryFilter.toLowerCase()) !== -1,
                            ) ?? [],
                    };
                })
                .filter((x) => x.items.length > 0);
            setFilterCategories(items);

            if (items.length > 0 && categoryFilter.length > 0) {
                setExpanded(items[0].id);
            }
        }, [categoryFilter]);

        useEffect(() => {
            if (defaultCategory) changeSelectedCategory(defaultCategory.parentCategory, defaultCategory.categoryId);
        }, [defaultCategory]);

        useEffect(() => {
            setCheckboxes(filterValues.filter((x) => x.label.toLowerCase().indexOf(itemFilter.toLowerCase()) !== -1));
        }, [itemFilter, filterValues]);

        useEffect(() => {
            if (selectedCategory?.id != null) {
                const existingChanges = trackedChangesList.find(
                    (tcl) => tcl.fieldKey === selectedCategory.fieldKey && tcl.valueKey === selectedCategory.valueKey,
                );
                const filteredCategoryValues = [
                    ...new Set(
                        filteredDataExcludingSelectedCategory
                            .map((obj) => obj[selectedCategory?.fieldKey])
                            .flatMap((v) => getCategoryValue(v)),
                    ),
                ];

                const values = data
                    .map((obj) => obj[selectedCategory?.fieldKey])
                    .flatMap((v) => getCategoryValue(v))
                    .filter((v) => v !== null || !selectedCategory.excludeBlank)
                    .sort((a, b) => {
                        if (selectedCategory.valueOrder) {
                            const toOrderIndex = (value: unknown) => {
                                const index = selectedCategory.valueOrder!.indexOf(value);
                                return index === -1 ? Infinity : index;
                            };
                            return toOrderIndex(a) - toOrderIndex(b);
                        }
                        return selectedCategory.dataType === FieldDefinitionFieldTypeNumeric.Date
                            ? String(b).localeCompare(String(a))
                            : String(a).localeCompare(String(b));
                    });

                if (values.includes(null)) {
                    values.unshift(null);
                }

                const uniqueValues: FilterValue[] = [...new Set(values)].map((value, index) => {
                    const valueFound = existingChanges && existingChanges.uncheckedValues.includes(value);
                    const checked = existingChanges ? !valueFound : true;
                    return {
                        id: index,
                        label: calculateLabel(selectedCategory, value),
                        value,
                        checked,
                        exclusion:
                            filteredCategoryValues.findIndex((fcv) => fcv === value) === -1
                                ? FilterUIExclusion.Value
                                : FilterUIExclusion.None,
                    };
                });
                setFilterValues(uniqueValues);
                setTrackedChanges(
                    existingChanges ?? {
                        fieldKey: selectedCategory.fieldKey,
                        valueKey: selectedCategory.valueKey,
                        uncheckedValues: [],
                        dataType: selectedCategory.dataType,
                        parentId: selectedCategoryParent.current,
                    },
                );
            }
        }, [selectedCategory]);

        const closeFilter = () => {
            onClose();
        };

        useOnclickOutside(
            cardRef.current?.element,
            () => {
                closeFilter();
            },
            (target: Node) => {
                const chipOrAdvancedSelect =
                    filterChipElements?.current
                        ?.values()
                        .toArray()
                        .some((x) => x.contains(target)) || advancedSelectRef.current?.contains(target);
                if (chipOrAdvancedSelect) return chipOrAdvancedSelect;

                // Now look for any element within the `filter-ui-excluded` elements
                // This should be dropdowns within the Advanced Select, but they're created
                // at the document level. We can't use the appendTo either due to clipping
                const excludedDropdowns = document.querySelector('.filter-ui-excluded');
                if (excludedDropdowns) return excludedDropdowns.contains(target);

                return false;
            },
        );

        useEffect(() => {
            const subscription = PopupService.closePopupsObservable().subscribe(() => closeFilter());

            return () => {
                subscription.unsubscribe();
            };
        }, []);

        const selectFilterValues = (checked: boolean) => {
            if (!selectedCategory) return;

            // If there are no checkboxes that aren't excluded. Do nothing
            if (!checkboxes.find((x) => x.exclusion !== FilterUIExclusion.Value)) return;

            const changedValues: unknown[] = [];
            setCheckboxes((prevState) =>
                prevState.map((x) => {
                    if (x.exclusion !== FilterUIExclusion.Value && x.checked !== checked) {
                        x.checked = checked;
                        changedValues.push(x.value);
                    }

                    return x;
                }),
            );

            const newChanges = {
                fieldKey: selectedCategory.fieldKey,
                valueKey: selectedCategory.valueKey,
                uncheckedValues: checked ? [] : [...trackedChanges.uncheckedValues, ...changedValues],
                dataType: selectedCategory.dataType,
                parentId: selectedCategoryParent.current,
            };

            // A filtered (un)select all will remain as tracked changes and passed on the next Modify request
            if (checkboxes.length !== filterValues.length) {
                if (checked) {
                    newChanges.uncheckedValues = trackedChanges.uncheckedValues.filter(
                        (x) => changedValues.indexOf(x) === -1,
                    );
                } else {
                    newChanges.uncheckedValues = trackedChanges.uncheckedValues.concat(changedValues);
                }
            }

            trackChangesChanged.current = true;
            const items = trackedChangesList.filter(
                (cl) => cl.fieldKey !== selectedCategory.fieldKey || cl.valueKey !== selectedCategory.valueKey,
            );
            const trackedChangeList = [...items, newChanges];
            setTrackedChangesList(trackedChangeList);
            setTrackedChanges(newChanges);
        };

        const onCheckboxChange = (value: boolean, filterValue: unknown) => {
            if (!selectedCategory) return;
            setCheckboxes((prevState) =>
                prevState.map((x) => {
                    if (x.value === filterValue) {
                        x.checked = value;
                    }
                    return x;
                }),
            );

            const changes: FilterUIStateUpdate = trackedChanges;
            if (value) {
                const index = trackedChanges.uncheckedValues.indexOf(filterValue);
                if (index > -1) {
                    changes.uncheckedValues.splice(index, 1);
                }
            } else {
                changes.uncheckedValues.push(filterValue);
            }

            trackChangesChanged.current = true;
            const existingChanges = {
                fieldKey: selectedCategory.fieldKey,
                valueKey: selectedCategory.valueKey,
                uncheckedValues: changes.uncheckedValues,
                dataType: selectedCategory.dataType,
                parentId: selectedCategoryParent.current,
            };
            const items = trackedChangesList.filter(
                (cl) => cl.fieldKey !== selectedCategory.fieldKey || cl.valueKey !== selectedCategory.valueKey,
            );
            const trackedChangeList = [...items, existingChanges];
            setTrackedChangesList(trackedChangeList);
            setTrackedChanges(changes);
        };

        const applyDefaultExclusions = (categoryList: FilterCategory[]) => {
            const newTrackedChangesList: FilterUIStateUpdate[] = [];
            categoryList.forEach((category) => {
                const categoriesWithDefaults = category.items.filter(
                    (c) => c.defaultUncheckedIds && c.defaultUncheckedIds?.length > 0,
                );
                categoriesWithDefaults.forEach((cat) => {
                    newTrackedChangesList.push({
                        fieldKey: cat.fieldKey,
                        valueKey: cat.valueKey,
                        uncheckedValues: [...cat.defaultUncheckedIds!],
                        dataType: cat.dataType,
                        parentId: category.id,
                    });
                });
            });
            return newTrackedChangesList;
        };

        const onClearClick = () => {
            // Do this before the request instead of after. If users toggle checkbox values while the http request is ongoing
            // we don't want to wipe out those changes
            const changes = {
                ...trackedChanges,
                uncheckedValues: selectedCategory?.defaultUncheckedIds ? [...selectedCategory.defaultUncheckedIds] : [],
            };
            setTrackedChangesList(applyDefaultExclusions(categories));
            setTrackedChanges(changes);

            if (selectedCategory) {
                // This isn't great, we want to somehow trigger the virtual list to re-render
                setCheckboxes((prevState) =>
                    prevState.map((x) => ({
                        ...x,
                        checked: !selectedCategory.defaultUncheckedIds?.includes(x.value),
                    })),
                );
            }
        };

        const onApplyClick = () => {
            setShowLoading(true);
            currentTrackedChangesList.current = trackedChangesList.filter((field) => field.uncheckedValues.length > 0);
            onApply(calculateFilters());
        };

        const onCategorySelect = (id: number) => {
            const cat = categories.flatMap((c) => c.items).find((c) => c.id === id);
            const parentId = categories.find((x) => x.items.includes(cat!))?.id;

            if (selectedCategory === undefined) {
                setTrackedChanges({
                    fieldKey: cat?.fieldKey,
                    valueKey: cat?.valueKey,
                    uncheckedValues: [],
                    dataType: cat?.dataType,
                    parentId,
                });
            }

            if (trackChangesChanged.current) {
                trackChangesChanged.current = false;
            }

            selectedCategoryParent.current = parentId;
            setSelectedCategory(cat);
            setShowAdvancedSelect(false);

            if (listRef?.current)
                listRef.current.scrollToRow({
                    index: 0,
                });
        };

        const onAdvancedSelectClick = () => {
            setShowAdvancedSelect(!showAdvancedSelect);
        };

        const onAdvancedSelectApply = (filterData: AdvanceSelectFilter) => {
            if (!selectedCategory) return;

            if (filterData.filters.length > 0) {
                const checkedValues: Array<unknown> = [];
                const uncheckedValues: Array<unknown> = [];
                // Apply the select across an unfiltered group
                filterValues.forEach((x) => {
                    if (x.exclusion !== FilterUIExclusion.Value) {
                        const matchesFilter = utils.ItemMatchesFilter(x, filterData);
                        if (matchesFilter && !x.checked) {
                            x.checked = true;
                            checkedValues.push(x.value);
                        } else if (!matchesFilter && x.checked) {
                            x.checked = false;
                            uncheckedValues.push(x.value);
                        }
                    }
                });

                trackChangesChanged.current = true;
                const uncheckedValuesList = trackedChanges.uncheckedValues
                    .filter((u) => checkedValues.indexOf(u) === -1)
                    .concat(uncheckedValues);

                const existingChanges = {
                    fieldKey: selectedCategory.fieldKey,
                    valueKey: selectedCategory.valueKey,
                    uncheckedValues: uncheckedValuesList,
                    dataType: selectedCategory.dataType,
                    parentId: selectedCategoryParent.current,
                };
                const items = trackedChangesList.filter(
                    (cl) => cl.fieldKey !== selectedCategory.fieldKey || cl.valueKey !== selectedCategory.valueKey,
                );
                const trackedChangeList = [...items, existingChanges];
                setTrackedChangesList(trackedChangeList);

                // Update tracked changes, only adding new changes
                setTrackedChanges({
                    ...trackedChanges,
                    uncheckedValues: uncheckedValuesList,
                });

                // Re-apply any item filter
                if (itemFilter) {
                    setCheckboxes(
                        filterValues.filter((x) => x.label.toLowerCase().indexOf(itemFilter.toLowerCase()) !== -1),
                    );
                } else {
                    setCheckboxes(filterValues);
                }
            }

            setShowAdvancedSelect(false);
        };

        type RowProps = { index?: number; style?: CSSProperties };

        const CheckboxRow = ({ index, style }: RowProps) => {
            if (!selectedCategory || index == null) return null;
            const x = checkboxes[index];

            return (
                <div style={{ ...style, padding: `${CHECKBOX_ROW_PADDING}px` }}>
                    <Tooltip position="top" parentTitle style={{ maxWidth: '40vw' }}>
                        <span title={x.label} className={styles.opraScreenFilterCheckbox}>
                            <CoreCheckbox
                                data-testid={`checkbox-${index}`}
                                label={x.label}
                                value={x.checked}
                                key={index}
                                onChange={(e) => onCheckboxChange(e.value, x.value)}
                                disabled={x.exclusion === FilterUIExclusion.Value}
                                labelClassName="k-overflow-hidden k-text-ellipsis"
                                defaultValue
                                alternativeStyle={false}
                            />
                        </span>
                    </Tooltip>
                </div>
            );
        };

        const emptyStateLeftSide = (
            <InfoField message={t('cm:componentsClean.screenFilter.noDataFound')} type="Info" layout="center" />
        );

        const emptyStateRightSide = (
            <InfoField
                message={
                    selectedCategory
                        ? !checkboxes.length && !itemFilter.length
                            ? t('cm:componentsClean.screenFilter.noValuesForDataset')
                            : t('cm:componentsClean.screenFilter.noDataFound')
                        : t('screenFilterNotPicked')
                }
                type="Info"
                layout="center"
            />
        );

        return (
            <Card
                ref={cardRef}
                data-testid={testid}
                title={t('filterCategories')}
                className={styles.cardWrapper}
                padding="none"
                headerActions={
                    <div className="k-d-flex k-align-items-center k-justify-content-center k-gap-2">
                        {headerExtra}
                        {headerExtra && (
                            <div style={{ width: 1, height: 20, backgroundColor: 'var(--kendo-color-border)' }} />
                        )}
                        <Button primary data-testid="close-button" onClick={closeFilter}>
                            {t('close')}
                        </Button>
                    </div>
                }
                footerActions={
                    <div className={styles.footerActionsWrapper}>
                        <Button data-testid="clear-button" onClick={onClearClick}>
                            {t('reset')}
                        </Button>
                        <Button data-testid="apply-button" primary onClick={onApplyClick}>
                            {t('apply')}
                        </Button>
                    </div>
                }
            >
                <div className={styles.contentWrapper}>
                    <div className={styles.leftContent}>
                        <SearchBox
                            data-testid="search-box-left"
                            fillMode="flat"
                            placeholder={t('cm2EnterValue')}
                            showSearchIcon
                            value={categoryFilter}
                            onChange={(e: TextBoxChangeEvent) => {
                                if (e.value as string) {
                                    setCategoryFilter(e.value as string);
                                } else {
                                    setCategoryFilter(e.target.value as string);
                                }
                            }}
                            style={{ width: '100%' }}
                            debounceTime={200}
                        />
                        <div className={cn(styles.categoryPanels, 'k-flex-col', 'k-gap-1')}>
                            {filterCategories.length === 0 && emptyStateLeftSide}
                            {filterCategories.map((c) => {
                                return (
                                    <ExpansionPanel
                                        title={getExpansionPanelTitle(c.id)}
                                        expanded={expanded === c.id}
                                        key={c.id}
                                        className="opra-expansion-panel"
                                        onAction={(event: ExpansionPanelActionEvent) => {
                                            setExpanded(event.expanded ? undefined : c.id);
                                        }}
                                    >
                                        {expanded === c.id && (
                                            <ExpansionPanelContent
                                                onKeyDown={(event) => {
                                                    // Stop the Enter or Space key from propagating
                                                    // Otherwise the expansion panel will close, instead of
                                                    // interacting with the child elements
                                                    if (event.code === 'Enter' || event.code === 'Space') {
                                                        event.stopPropagation();
                                                    }
                                                }}
                                                className={styles.expansionPanelContent}
                                            >
                                                <InteractiveList
                                                    data={c.items.sort((a, b) => a.index - b.index)}
                                                    onSelect={onCategorySelect}
                                                    selectedId={selectedCategory?.id}
                                                />
                                            </ExpansionPanelContent>
                                        )}
                                    </ExpansionPanel>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.rightContent}>
                        <SearchBox
                            data-testid="search-box-right"
                            fillMode="flat"
                            placeholder={t('cm2EnterValue')}
                            showSearchIcon
                            value={itemFilter}
                            onChange={(e: TextBoxChangeEvent) => {
                                if (e.value as string) {
                                    setItemFilter(e.value as string);
                                } else {
                                    setItemFilter(e.target.value as string);
                                }
                            }}
                            style={{ width: '100%' }}
                            debounceTime={200}
                        />
                        <div className={styles.filterButtonsWrapper} ref={advancedSelectAnchor}>
                            <Button
                                data-testid="unselect-all"
                                onClick={() => selectFilterValues(false)}
                                disabled={!checkboxes.length}
                            >
                                {t('unselectAll')}
                            </Button>
                            <Button
                                data-testid="select-all"
                                onClick={() => selectFilterValues(true)}
                                disabled={!checkboxes.length}
                            >
                                {t('selectAll')}
                            </Button>
                            <Button
                                data-testid="advanced-select"
                                primary
                                onClick={onAdvancedSelectClick}
                                disabled={!checkboxes.length}
                            >
                                {t('advancedSelect')}
                            </Button>
                        </div>
                        <div className={styles.checkboxListWrapper}>
                            {showLoading && <Loading />}
                            {!showLoading && (checkboxes.length === 0 || !selectedCategory) && emptyStateRightSide}
                            {!showLoading && checkboxes.length > 0 && (
                                <List
                                    className={styles.autoSizeWillChange}
                                    rowCount={checkboxes.length}
                                    rowHeight={ROW_SIZE}
                                    listRef={listRef}
                                    rowProps={{}}
                                    rowComponent={CheckboxRow}
                                    style={{ height: LIST_HEIGHT }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {selectedCategory && (
                    <AdvancedSelect
                        ref={advancedSelectRef}
                        key={selectedCategory.id}
                        anchor={advancedSelectAnchor.current}
                        dataType={selectedCategory.dataType}
                        show={showAdvancedSelect}
                        anchorAlign={{ vertical: 'top', horizontal: 'right' }}
                        popupAlign={{ vertical: 'top', horizontal: 'left' }}
                        onCancel={() => setShowAdvancedSelect(false)}
                        onSelect={onAdvancedSelectApply}
                    />
                )}
            </Card>
        );
    },
);
